// src/components/CookieConsent.jsx
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon } from '@heroicons/react/24/outline';

const COOKIE_CONSENT_KEY = 'smilesflow_cookie_consent';
const CONSENT_VERSION = '1'; // Increment when policy changes to re-prompt

// Routes where cookie banner should NOT appear (player-facing pages)
const EXCLUDED_ROUTES = ['/p/', '/play/', '/g/', '/leaderboard/'];

/**
 * Cookie Consent Banner - GDPR Compliance
 * Shows banner for new visitors, stores consent in localStorage
 * Excludes play pages for better player experience
 */
const CookieConsent = () => {
  const location = useLocation();
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, can't be disabled
    analytics: false,
    marketing: false
  });

  // Check if current route is excluded (play pages)
  const isExcludedRoute = EXCLUDED_ROUTES.some(route => 
    location.pathname.startsWith(route)
  );

  useEffect(() => {
    // Don't show on play pages
    if (isExcludedRoute) {
      setShowBanner(false);
      return;
    }

    // Check if user has already consented
    const storedConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
    
    if (storedConsent) {
      try {
        const parsed = JSON.parse(storedConsent);
        // Check if consent version matches
        if (parsed.version === CONSENT_VERSION) {
          setPreferences(parsed.preferences);
          // Apply stored preferences
          applyPreferences(parsed.preferences);
          return;
        }
      } catch (e) {
        // Invalid stored consent, show banner
      }
    }
    
    // No valid consent found, show banner after a short delay
    const timer = setTimeout(() => setShowBanner(true), 1000);
    return () => clearTimeout(timer);
  }, [isExcludedRoute]);

  const applyPreferences = (prefs) => {
    // Enable/disable analytics based on consent
    if (typeof window !== 'undefined') {
      window.cookieConsent = prefs;
      
      // Dispatch event for analytics to listen to
      window.dispatchEvent(new CustomEvent('cookieConsentUpdate', { 
        detail: prefs 
      }));

      // Update Google Consent Mode if available
      if (window.gtag) {
        window.gtag('consent', 'update', {
          analytics_storage: prefs.analytics ? 'granted' : 'denied',
          ad_storage: prefs.marketing ? 'granted' : 'denied'
        });
      }
    }
  };

  const saveConsent = (prefs) => {
    const consentData = {
      version: CONSENT_VERSION,
      preferences: prefs,
      timestamp: new Date().toISOString()
    };
    
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consentData));
    setPreferences(prefs);
    applyPreferences(prefs);
    setShowBanner(false);
    setShowPreferences(false);
  };

  const acceptAll = () => {
    saveConsent({
      necessary: true,
      analytics: true,
      marketing: true
    });
  };

  const acceptNecessary = () => {
    saveConsent({
      necessary: true,
      analytics: false,
      marketing: false
    });
  };

  const savePreferences = () => {
    saveConsent(preferences);
  };

  // Don't render on excluded routes or if banner shouldn't show
  if (isExcludedRoute || !showBanner) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6"
        style={{ fontFamily: '"Inter", sans-serif' }}
      >
        <div className="max-w-4xl mx-auto gch-bg-card rounded-xl shadow-2xl border border-[var(--admin-border)] overflow-hidden">
          {!showPreferences ? (
            // Main banner
            <div className="p-4 md:p-6">
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex-1">
                  <h3 className="text-base font-semibold gch-text-primary mb-1" style={{ fontFamily: '"Lexend", sans-serif' }}>
                    🍪 Cookie Settings
                  </h3>
                  <p className="text-sm gch-text-secondary">
                    We use cookies to improve your experience and analyze site usage. 
                    You can customize your preferences or accept all cookies.
                  </p>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    onClick={() => setShowPreferences(true)}
                    className="px-4 py-2 text-sm font-semibold gch-text-primary bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                  >
                    Customize
                  </button>
                  <button
                    onClick={acceptNecessary}
                    className="px-4 py-2 text-sm font-semibold gch-text-primary border border-[var(--admin-border)] hover:bg-white/5 rounded-lg transition-colors"
                  >
                    Necessary Only
                  </button>
                  <button
                    onClick={acceptAll}
                    className="px-4 py-2 text-sm font-semibold text-white bg-[var(--admin-cta-solid)] hover:bg-[var(--admin-cta-solid)] rounded-lg transition-colors"
                  >
                    Accept All
                  </button>
                </div>
              </div>
            </div>
          ) : (
            // Preferences panel
            <div className="p-4 md:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold gch-text-primary" style={{ fontFamily: '"Lexend", sans-serif' }}>
                  Cookie Preferences
                </h3>
                <button
                  onClick={() => setShowPreferences(false)}
                  className="p-1 hover:bg-white/5 rounded-lg transition-colors"
                >
                  <XMarkIcon className="w-5 h-5 gch-text-secondary" />
                </button>
              </div>
              
              <div className="space-y-4 mb-6">
                {/* Necessary cookies */}
                <div className="flex items-start justify-between p-3 bg-white/5 rounded-lg">
                  <div className="flex-1">
                    <div className="text-sm font-semibold gch-text-primary">Necessary Cookies</div>
                    <div className="text-xs gch-text-secondary mt-0.5">
                      Required for the site to function. Cannot be disabled.
                    </div>
                  </div>
                  <div className="ml-4">
                    <div className="w-10 h-6 bg-green-500 rounded-full flex items-center justify-end px-1">
                      <div className="w-4 h-4 bg-white rounded-full shadow" />
                    </div>
                  </div>
                </div>

                {/* Analytics cookies */}
                <div className="flex items-start justify-between p-3 border border-[var(--admin-border)] rounded-lg">
                  <div className="flex-1">
                    <div className="text-sm font-semibold gch-text-primary">Analytics Cookies</div>
                    <div className="text-xs gch-text-secondary mt-0.5">
                      Help us understand how visitors use our site to improve it.
                    </div>
                  </div>
                  <div className="ml-4">
                    <button
                      onClick={() => setPreferences(p => ({ ...p, analytics: !p.analytics }))}
                      className={`w-10 h-6 rounded-full flex items-center px-1 transition-colors ${
                        preferences.analytics ? 'bg-green-500 justify-end' : 'bg-white/10 justify-start'
                      }`}
                    >
                      <div className="w-4 h-4 bg-white rounded-full shadow" />
                    </button>
                  </div>
                </div>

                {/* Marketing cookies */}
                <div className="flex items-start justify-between p-3 border border-[var(--admin-border)] rounded-lg">
                  <div className="flex-1">
                    <div className="text-sm font-semibold gch-text-primary">Marketing Cookies</div>
                    <div className="text-xs gch-text-secondary mt-0.5">
                      Used to deliver relevant ads and track campaign effectiveness.
                    </div>
                  </div>
                  <div className="ml-4">
                    <button
                      onClick={() => setPreferences(p => ({ ...p, marketing: !p.marketing }))}
                      className={`w-10 h-6 rounded-full flex items-center px-1 transition-colors ${
                        preferences.marketing ? 'bg-green-500 justify-end' : 'bg-white/10 justify-start'
                      }`}
                    >
                      <div className="w-4 h-4 bg-white rounded-full shadow" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setShowPreferences(false)}
                  className="px-4 py-2 text-sm font-semibold gch-text-primary hover:bg-white/5 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={savePreferences}
                  className="px-4 py-2 text-sm font-semibold text-white bg-[var(--admin-cta-solid)] hover:bg-[var(--admin-cta-solid)] rounded-lg transition-colors"
                >
                  Save Preferences
                </button>
              </div>
            </div>
          )}
          
          {/* Privacy policy link */}
          <div className="px-4 md:px-6 py-3 bg-white/5 border-t border-[var(--admin-border)]">
            <a 
              href="/privacy" 
              target="_blank"
              className="text-xs text-[var(--admin-accent)] hover:text-[var(--admin-accent)] font-medium"
            >
              Read our Privacy Policy →
            </a>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CookieConsent;

/**
 * Hook to check cookie consent status
 */
export const useCookieConsent = () => {
  const [consent, setConsent] = useState({
    necessary: true,
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    // Check stored consent
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed.version === CONSENT_VERSION) {
          setConsent(parsed.preferences);
        }
      } catch (e) {
        // Use defaults
      }
    }

    // Listen for consent updates
    const handleUpdate = (e) => {
      setConsent(e.detail);
    };
    
    window.addEventListener('cookieConsentUpdate', handleUpdate);
    return () => window.removeEventListener('cookieConsentUpdate', handleUpdate);
  }, []);

  return consent;
};
